import { BulletinsService } from 'src/services/bulletins.service';
import { FULL_NAME } from 'src/config';
import { HttpStatusCode } from 'src/utils/enums/http-method.enum';

export const BULLETIN_DOCUMENT_TYPE = 1;

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB;
const DIRECT_UPLOAD_LIMIT_MB = 3;
const MAX_DOCUMENT_MB = 10;
const DEFAULT_MODIFIED_BY = 'DE Portal';

export const DIRECT_UPLOAD_LIMIT_BYTES = DIRECT_UPLOAD_LIMIT_MB * BYTES_PER_MB;
export const MAX_DOCUMENT_BYTES = MAX_DOCUMENT_MB * BYTES_PER_MB;

// The upload endpoint only accepts these characters in `modifiedBy`.
export const getModifiedBy = (): string =>
    (window.sessionStorage.getItem(FULL_NAME) ?? '')
        .replace(/[^0-9a-zA-Z=/._+ -]/g, '')
        .trim() || DEFAULT_MODIFIED_BY;

const assertSuccess = (status: number, step: string) => {
    if (status !== HttpStatusCode.SUCCESS) {
        throw new Error(`Bulletin document upload failed at ${step}`);
    }
};

/** Same flow as static PDFs: direct up to 3 MB, S3 presigned URL above that. */
export const uploadBulletinDocument = async (
    service: BulletinsService,
    file: File,
    documentId: string,
): Promise<void> => {
    const modifiedBy = getModifiedBy();

    if (file.size <= DIRECT_UPLOAD_LIMIT_BYTES) {
        const response = await service.uploadDocument(
            documentId,
            BULLETIN_DOCUMENT_TYPE,
            modifiedBy,
            file,
        );

        assertSuccess(response.status, 'upload');
        return;
    }

    // The presign endpoint only accepts [\w-. ]+.pdf names.
    const presigned = await service.getUploadPresignedUrl(
        documentId,
        BULLETIN_DOCUMENT_TYPE,
        `${documentId}.pdf`,
    );

    assertSuccess(presigned.status, 'presigned URL');

    const s3Response = await fetch(presigned.data.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
            'Content-Type': 'application/pdf',
            'x-amz-server-side-encryption': 'aws:kms',
            'x-amz-server-side-encryption-aws-kms-key-id':
                presigned.data.kmsKeyId || '',
        },
    });

    if (!s3Response.ok) {
        throw new Error('Bulletin document upload failed at S3');
    }

    const registered = await service.uploadDocumentWithS3Key(
        documentId,
        BULLETIN_DOCUMENT_TYPE,
        modifiedBy,
        presigned.data.s3Key,
    );

    assertSuccess(registered.status, 'registration');
};
